import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import moment from "moment";

export const SubscriptionContext = createContext();

const FREE_ACCOUNT = { name: "Free Account" };

// Statuses that grant access while ends_at is still in the future.
const ACTIVE_STATUSES = ["active", "trialing"];
// Product decision: a user who cancelled (cancel_at_period_end) has already
// paid through ends_at, so they keep access until then. Remove this list
// (set to []) if cancelled users should lose access immediately.
const PAID_THROUGH_STATUSES = ["cancelled", "canceled"];

// setTimeout max delay is 2^31 - 1 ms (~24.8 days)
const MAX_TIMEOUT = 2147483647;

const computeIsSubscribed = (subscription) => {
  const sub = subscription?.subscription;
  if (!sub) return false;

  const status = String(sub.status || "").toLowerCase();
  const allowed = [...ACTIVE_STATUSES, ...PAID_THROUGH_STATUSES];
  if (!allowed.includes(status)) return false;

  if (!sub.ends_at) return false;

  // ASSUMPTION: the API stores ends_at in UTC (Laravel default).
  // Parsing as local time would be off by the user's UTC offset.
  const endsAt = moment.utc(sub.ends_at);
  return endsAt.isValid() && endsAt.isAfter(moment.utc());
};

export const SubscriptionProvider = ({ children }) => {
  const { http } = global.services;

  const [subscription, setSubscription] = useState(null);
  const [isFreemium, setFreemium] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [showPremiumPopUp, setPopUpPrem] = useState(false);
  const [viewPremiumBar, setPremium] = useState(true);
  const [viewPremiumFooter, setPremiumFooter] = useState(true);
  const [closeAlertBar, setCloseBar] = useState(true);
  // false until the first fetch settles, so the UI can avoid flashing
  // "unsubscribed" content or redirecting a paying user too early
  const [isSubscriptionLoaded, setSubscriptionLoaded] = useState(false);
  // bumped to force isSubscribed to re-evaluate when ends_at passes
  const [tick, setTick] = useState(0);

  // Incremented on reset so in-flight requests from a previous session
  // can't write stale data after logout.
  const sessionRef = useRef(0);

  const isSubscribed = useMemo(() => {
    try {
      if (isFreemium) return true;
      return computeIsSubscribed(subscription);
    } catch (e) {
      console.error("Error@subscription", e);
      return false; // fail closed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, isFreemium, tick]);

  // Re-evaluate exactly when the subscription expires during a session
  useEffect(() => {
    const endsAt = subscription?.subscription?.ends_at;
    if (!endsAt) return undefined;

    const msLeft = moment.utc(endsAt).diff(moment.utc());
    if (msLeft <= 0) return undefined;

    const id = setTimeout(() => setTick((t) => t + 1), Math.min(msLeft + 1000, MAX_TIMEOUT));
    return () => clearTimeout(id);
  }, [subscription]);

  const showPremiumPopUpHandler = useCallback((profile) => {
    if (profile && profile.login_count > 1 && Boolean(profile.show_premium) === false) {
      setPopUpPrem(true);
    }
  }, []);

  // Full reset: call on logout. Clears everything that could leak into the next user.
  const resetSubscription = useCallback(() => {
    sessionRef.current += 1;
    setSubscription(FREE_ACCOUNT);
    setFreemium(false); // previously never reset: next login stayed "subscribed"
    setBillingHistory([]);
    setPopUpPrem(false);
    setPremium(true);
    setPremiumFooter(true);
    setCloseBar(true);
    setSubscriptionLoaded(false);
  }, []);

  const getSubscription = useCallback(async () => {
    const session = sessionRef.current;
    try {
      const { data } = await http.get("public/subscribes");
      if (session !== sessionRef.current) return; // logged out meanwhile

      if (!_.isEmpty(data.payment_method) || !_.isEmpty(data.subscription)) {
        setSubscription(data);
      } else {
        setSubscription(FREE_ACCOUNT);
      }
    } catch (error) {
      console.error("getSubscription", error);
      if (session !== sessionRef.current) return;

      // Auth failure: don't keep any previous entitlement around
      const code = error?.response?.status;
      if (code === 401 || code === 403) {
        setSubscription(FREE_ACCOUNT);
        setFreemium(false);
      }
    } finally {
      if (session === sessionRef.current) setSubscriptionLoaded(true);
    }
  }, [http]);

  // NOTE: client-side only. Any API route that serves paid features must
  // enforce subscription on the server as well.
  const updateFreemium = useCallback(() => {
    setFreemium(true);
  }, []);

  const updateSubscription = useCallback((payload) => {
    setSubscription(payload);
  }, []);

  const getBillingHistory = useCallback(async () => {
    const session = sessionRef.current;
    try {
      const { data } = await http.get("public/billing-history");
      if (session !== sessionRef.current) return;
      setBillingHistory(data.data);
    } catch (error) {
      console.error("getBillingHistory", error);
    }
  }, [http]);

  const value = useMemo(
    () => ({
      isSubscribed,
      isSubscriptionLoaded,
      subscription,
      billingHistory,
      getSubscription,
      updateFreemium,
      getBillingHistory,
      resetSubscription,
      updateSubscription,
      showPremiumPopUp,
      showPremiumPopUpHandler,
      setPopUpPrem,
      viewPremiumBar,
      setPremium,
      closeAlertBar,
      setCloseBar,
      setPremiumFooter,
      viewPremiumFooter,
    }),
    [
      isSubscribed,
      isSubscriptionLoaded,
      subscription,
      billingHistory,
      getSubscription,
      updateFreemium,
      getBillingHistory,
      resetSubscription,
      updateSubscription,
      showPremiumPopUp,
      showPremiumPopUpHandler,
      viewPremiumBar,
      closeAlertBar,
      viewPremiumFooter,
    ]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
